/* Terminal shell — the reference frame.
 *
 * The trader portal is laid out the way the AlgoBulls console is: a white top
 * bar (brand, the AI-builder call to action, theme switch, help, alerts, the
 * account menu), a white sidebar whose groups are captioned in small grey type
 * and separated by hairlines with the active entry drawn as a solid brand-blue
 * pill, and a white page carrying a large title with a blue ⓘ.
 *
 * What the reference does not have, and this shell keeps, are the things a
 * trading terminal cannot drop:
 *
 *   * the index strip — NIFTY, SENSEX, INDIA VIX — under the top bar;
 *   * the watchlist and the order ticket, which stay MOUNTED across every
 *     page (hidden, not unmounted, off the trading pages) so a selected
 *     instrument and a half-typed ticket survive navigation;
 *   * the status bar with day P&L, exposure and the kill switch, pinned to the
 *     bottom. A risk control that can be scrolled off screen is a risk control
 *     that is not there;
 *   * the trading-mode badge, always in the same place in the top bar.
 *
 * Function keys still switch pages — the muscle memory every terminal shares —
 * and ⌘K still opens the command palette.
 *
 * This shell is also where the blotter data comes from: orders, positions and
 * executions are polled here and pushed into the store, because they are read
 * on every page (dashboard, portfolio, blotters, status bar) and must not
 * depend on any one panel being mounted.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useStore } from '@/store'
import { api } from '@/lib/api'
import { Watchlist } from '@/panels/Watchlist'
import { Chart } from '@/panels/Chart'
import { OrderTicket } from '@/panels/OrderTicket'
import { Blotters } from '@/panels/Blotters'
import { DepthLadder } from '@/panels/DepthLadder'
import { OptionChain } from '@/panels/OptionChain'
import { MarketPulse } from '@/panels/MarketPulse'
import { HftMonitor } from '@/panels/HftMonitor'
import { Forge } from '@/panels/Forge'
import { Atlas } from '@/panels/Atlas'
import { Simulator } from '@/panels/Simulator'
import { Performance } from '@/panels/Performance'
import { Dashboard } from '@/panels/Dashboard'
import { MyPortfolio } from '@/panels/MyPortfolio'
import { BrokingDetails, HelpPage, SettingsPage } from '@/panels/Setup'
import { IndexStrip } from '@/components/IndexStrip'
import { ModeBadge } from '@/components/ModeBadge'
import { StatusDot } from '@/components/StatusDot'
import { KillSwitch } from '@/components/KillSwitch'
import { CommandPalette } from '@/components/CommandPalette'
import { Licences } from '@/components/Licences'
import { Icon, type IconName } from '@/components/Icons'
import { PRODUCTS } from '@/lib/products'
import { fmtMoney, fmtSigned } from '@/lib/format'

export type Page =
  | 'dashboard' | 'portfolio' | 'reports'
  | 'trade' | 'options' | 'markets' | 'hft' | 'sim'
  | 'atlas' | 'forge' | 'copilot'
  | 'broking' | 'settings' | 'help'

interface PageMeta {
  id: Page
  label: string
  icon: IconName
  /** Function key, where one exists. */
  key?: string
  title: string
  info: string
  /** Trading pages keep the watchlist and the ticket at their sides. */
  trading?: boolean
}

interface NavGroup { group: string; items: PageMeta[] }

const NAV: NavGroup[] = [
  {
    group: 'Algo Products', items: [
      { id: 'atlas', label: `${PRODUCTS.marketplace.name} (Marketplace)`, icon: 'store', key: 'F7',
        title: PRODUCTS.marketplace.full,
        info: `${PRODUCTS.marketplace.tagline} Every published figure carries its result class and period.` },
      { id: 'forge', label: PRODUCTS.studio.name, icon: 'blocks', key: 'F6',
        title: PRODUCTS.studio.full, info: PRODUCTS.studio.tagline },
      { id: 'copilot', label: `${PRODUCTS.studio.name} Copilot`, icon: 'sparkles',
        title: `${PRODUCTS.studio.name} Copilot`,
        info: 'Describe a strategy in a sentence. Copilot drafts the blocks, you review every one, and nothing is saved until you accept.' },
    ],
  },
  {
    group: 'Algo Execution & Analytics', items: [
      { id: 'dashboard', label: 'Dashboard', icon: 'grid',
        title: 'Dashboard',
        info: 'Your subscriptions, profile and account at a glance, the strategies you can run, and your recent P&L and trades.' },
      { id: 'portfolio', label: 'My Portfolio', icon: 'briefcase', key: 'F2',
        title: 'My Portfolio',
        info: 'Every strategy available to you with its mode, status and controls, and your open positions, allocation and margin beneath.' },
      { id: 'reports', label: 'Live Reports', icon: 'report', key: 'F5',
        title: 'Live Reports',
        info: 'P&L, ratios and execution quality for the period you choose, with the methodology behind every figure shown.' },
    ],
  },
  {
    group: 'Trading', items: [
      { id: 'trade', label: 'Trade', icon: 'candles', key: 'F1', trading: true,
        title: 'Trade',
        info: 'Chart, depth ladder and blotters, with the watchlist and order ticket at either side.' },
      { id: 'options', label: 'Option Chain', icon: 'layers', key: 'F3', trading: true,
        title: 'Option Chain',
        info: 'Calls and puts by strike with greeks, for the selected underlying and expiry.' },
      { id: 'markets', label: 'Markets', icon: 'pulse', key: 'F4', trading: true,
        title: 'Markets',
        info: 'Indices, movers and the sector heatmap. Levels from the simulator are labelled as such.' },
      { id: 'hft', label: 'HFT Monitor', icon: 'rocket', key: 'F8', trading: true,
        title: 'HFT Monitor',
        info: 'Feed handler, clock, replica and watchdogs, with measured latency percentiles. Never theoretical.' },
      { id: 'sim', label: 'Simulator', icon: 'flask', key: 'F9',
        title: 'Simulator',
        info: 'Day-trading practice with virtual capital. Everything here is clearly simulated.' },
    ],
  },
  {
    group: 'Setup', items: [
      { id: 'broking', label: 'Broking Details', icon: 'link',
        title: 'Broking Details',
        info: 'The trading accounts you hold access to, their mode, balances and margin.' },
      { id: 'settings', label: 'Settings', icon: 'settings',
        title: 'Settings',
        info: 'Appearance, keyboard shortcuts and your session.' },
    ],
  },
  {
    group: 'Extra', items: [
      { id: 'help', label: 'Help', icon: 'help',
        title: 'Help',
        info: 'Shortcuts, where things live, and the open-source notices this product is obliged to show.' },
    ],
  },
]

const PAGES: Record<Page, PageMeta> = Object.fromEntries(
  NAV.flatMap((g) => g.items).map((p) => [p.id, p]),
) as Record<Page, PageMeta>

const BLOTTER_POLL_MS = 5_000

export function Terminal({ user, onSignOut }: {
  user?: { email: string }
  onSignOut?: () => void
}) {
  const mode = useStore((s) => s.tradingMode)
  const theme = useStore((s) => s.theme)
  const setTheme = useStore((s) => s.setTheme)
  const setPalette = useStore((s) => s.setCommandPalette)
  const setBlotter = useStore((s) => s.setActiveBlotter)
  const health = useStore((s) => s.health)
  const positions = useStore((s) => s.positions)
  const orders = useStore((s) => s.orders)
  const setOrders = useStore((s) => s.setOrders)
  const setPositions = useStore((s) => s.setPositions)
  const setExecutions = useStore((s) => s.setExecutions)

  const [page, setPage] = useState<Page>('dashboard')
  const [licences, setLicences] = useState(false)
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('qorvex.sidebar') === 'collapsed' } catch { return false }
  })

  // The store starts in one theme and initTheme() may already have applied
  // another to <html>; adopt what is on the document so the switch is right.
  useEffect(() => {
    const applied = document.documentElement.getAttribute('data-theme')
    if ((applied === 'light' || applied === 'dark') && applied !== theme) setTheme(applied)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Function keys switch pages — the muscle memory every terminal shares.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const hit = NAV.flatMap((g) => g.items).find((p) => p.key === e.key)
      if (hit) { e.preventDefault(); setPage(hit.id) }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Blotter data. Polled here, not in a panel, so every page reads the same
  // live orders, positions and executions.
  const pollBlotters = useCallback(async () => {
    const [o, p, x] = await Promise.allSettled([
      api.listOrders({ limit: '200' }),
      api.listPositions(),
      api.listExecutions({ limit: '200' }),
    ])
    if (o.status === 'fulfilled') setOrders(o.value.items)
    if (p.status === 'fulfilled') setPositions(p.value.items)
    if (x.status === 'fulfilled') setExecutions(x.value.items)
  }, [setOrders, setPositions, setExecutions])

  useEffect(() => {
    let cancelled = false
    const tick = () => { if (!cancelled) void pollBlotters() }
    tick()
    const t = setInterval(tick, BLOTTER_POLL_MS)
    return () => { cancelled = true; clearInterval(t) }
  }, [pollBlotters])

  const toggleCollapsed = () => setCollapsed((c) => {
    try { localStorage.setItem('qorvex.sidebar', c ? 'open' : 'collapsed') } catch { /* non-fatal */ }
    return !c
  })

  const meta = PAGES[page]
  const trading = Boolean(meta.trading)

  const totalPnl = positions.reduce((a, p) => a + (Number(p.total_pnl) || 0), 0)
  const exposure = positions.reduce((a, p) => a + Math.abs(Number(p.exposure) || 0), 0)
  const working = orders.filter((o) =>
    ['SUBMITTED', 'ACKNOWLEDGED', 'PARTIALLY_FILLED', 'PENDING_AMEND', 'PENDING_CANCEL']
      .includes(o.status)).length
  const rejected = orders.filter((o) => o.status === 'REJECTED').length

  const openOrdersBlotter = () => { setBlotter('orders'); setPage('trade') }

  return (
    <div className={`qx-frame${collapsed ? ' collapsed' : ''}`}>
      {/* ---------- top bar ---------- */}
      <header className="qx-topbar">
        <div className="qx-brand">
          <span className="qx-brand-chip">
            <img src="/qorvex-mark.png" alt="" />
          </span>
          <span className="qx-brand-name">qorvex<sup>®</sup></span>
        </div>

        <button
          onClick={() => setPalette(true)}
          title="Search instruments and commands (⌘K)"
          className="qx-search"
          style={{
            marginLeft: 'var(--sp-3)', height: 34,
            border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-md)',
            background: 'var(--bg-input)', color: 'var(--text-muted)',
            fontFamily: 'inherit', fontSize: 'var(--fs-sm)', cursor: 'pointer',
            padding: '0 12px', justifyContent: 'space-between',
          }}
        >
          <span>Search instruments and commands</span>
          <kbd style={{
            padding: '1px 6px', background: 'var(--bg-active)', borderRadius: 4,
            fontSize: 'var(--fs-2xs)', fontFamily: 'var(--font-code)', color: 'var(--accent)',
          }}>
            ⌘K
          </kbd>
        </button>

        <div className="qx-topbar-right">
          <button className="qx-btn qx-btn-outline qx-topbar-copilot" onClick={() => setPage('copilot')}
                  title={`Open ${PRODUCTS.studio.name} Copilot`}>
            <Icon name="sparkles" size={16} />
            Create Strategies Using AI
          </button>

          <ModeBadge mode={mode} />

          <span style={{ display: 'inline-flex', gap: 'var(--sp-3)' }}>
            <StatusDot
              health={health.marketData === 'live' ? 'ok'
                    : health.marketData === 'stale' ? 'warn' : 'down'}
              label="Market data"
              detail={`Market data is ${health.marketData}`}
            />
            <StatusDot
              health={health.engine === 'healthy' ? 'ok'
                    : health.engine === 'degraded' ? 'warn' : 'down'}
              label="Engine"
              detail={`Trading engine is ${health.engine}`}
            />
          </span>

          <ThemeSwitch theme={theme} onToggle={() => setTheme(theme === 'dark' ? 'light' : 'dark')} />

          <button className="qx-iconbtn" title="Help" onClick={() => setPage('help')}>
            <Icon name="help" size={18} />
          </button>
          <button className="qx-iconbtn" title={`${PRODUCTS.marketplace.name}: ready-made strategies`}
                  onClick={() => setPage('atlas')}>
            <Icon name="bulb" size={18} />
          </button>
          <button className="qx-iconbtn" onClick={openOrdersBlotter}
                  title={rejected > 0
                    ? `${rejected} rejected order${rejected === 1 ? '' : 's'} — open the order blotter`
                    : 'No rejected orders'}>
            <Icon name="bell" size={18} />
            {rejected > 0 && <span className="badge">{rejected > 99 ? '99+' : rejected}</span>}
          </button>

          <span className="qx-topbar-sep" />
          <AccountMenu
            email={user?.email ?? 'trader'}
            mode={mode}
            onSignOut={onSignOut}
            onNavigate={setPage}
          />
        </div>
      </header>

      {/* ---------- sidebar ---------- */}
      <nav aria-label="Sections" className="qx-side">
        <button
          className="qx-side-collapse"
          onClick={toggleCollapsed}
          title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          aria-expanded={!collapsed}
        >
          <Icon name={collapsed ? 'chevron-right' : 'chevron-left'} size={14} />
        </button>

        {NAV.map((group) => (
          <div key={group.group} className="qx-nav-group">
            <div className="qx-nav-caption">{group.group}</div>
            {group.items.map((item) => {
              const on = page === item.id
              return (
                <button
                  key={item.id}
                  className={`qx-nav-item${on ? ' on' : ''}`}
                  onClick={() => setPage(item.id)}
                  title={item.key ? `${item.label} (${item.key})` : item.label}
                  aria-current={on ? 'page' : undefined}
                >
                  <Icon name={item.icon} size={16} />
                  {!collapsed && <span>{item.label}</span>}
                  {!collapsed && item.key && <kbd className="kbd">{item.key}</kbd>}
                </button>
              )
            })}
          </div>
        ))}
      </nav>

      {/* ---------- main ---------- */}
      <main className="qx-main" style={{ overflow: 'hidden' }}>
        {/* The band every Indian trading screen carries. It distinguishes a
            level from a market feed from one the simulator produced. */}
        <div style={{ flexShrink: 0, height: 34, borderBottom: '1px solid var(--border)' }}>
          <IndexStrip />
        </div>

        <div className={`qx-body-scroll${trading ? ' trading' : ''}`}>
          <div className="qx-page" style={{
            display: 'flex', flexDirection: 'column', minHeight: 0,
            padding: trading ? 'var(--sp-3) var(--sp-4)' : undefined,
          }}>
            {/* Page head. Trading pages get the compact form: the rows are
                the product, and a paragraph above them costs rows. */}
            <div className="qx-page-head" style={{ marginBottom: trading ? 'var(--sp-2)' : undefined }}>
              <div>
                {!trading && (
                  <div className="qx-crumbs"><b>Home</b><span>/</span><b>{meta.label}</b></div>
                )}
                <h1 className="qx-page-title" style={{ fontSize: trading ? 15 : undefined }}>
                  {meta.title}
                  <button type="button" className="qx-info" title={meta.info} aria-label={meta.info}>i</button>
                </h1>
                {!trading && <p className="qx-page-desc">{meta.info}</p>}
              </div>
            </div>

            {/* Body. The watchlist and ticket are OUTSIDE the page switch and
                only hidden off trading pages, so a selected instrument and a
                half-typed order survive navigation. */}
            <div className={`qx-trade-grid${trading ? ' trading' : ''}`}>
              <div className="qx-trade-slot watch">
                <Watchlist />
              </div>

              <div className="qx-trade-main">
                {page === 'trade' && (
                  <div className="qx-trade-inner">
                    <div className="chart"><Chart /></div>
                    <div className="depth"><DepthLadder /></div>
                    <div className="blotters"><Blotters /></div>
                  </div>
                )}
                {page === 'options' && <div style={{ minHeight: 0, overflow: 'auto' }}><OptionChain /></div>}
                {page === 'markets' && <div style={{ minHeight: 0, overflow: 'auto' }}><MarketPulse /></div>}
                {page === 'hft' && <div style={{ minHeight: 0, overflow: 'auto' }}><HftMonitor /></div>}

                {page === 'dashboard' && <Dashboard onNavigate={setPage} user={user} />}
                {page === 'portfolio' && <MyPortfolio onNavigate={setPage} />}
                {page === 'reports' && <Performance />}
                {page === 'sim' && <Simulator />}
                {page === 'atlas' && <Atlas />}
                {page === 'forge' && <Forge initialLane="CLASSIC" />}
                {page === 'copilot' && <Forge initialLane="COPILOT" />}
                {page === 'broking' && <BrokingDetails />}
                {page === 'settings' && (
                  <SettingsPage theme={theme} onTheme={setTheme} email={user?.email} pages={NAV} />
                )}
                {page === 'help' && <HelpPage pages={NAV} onLicences={() => setLicences(true)} />}
              </div>

              <div className="qx-trade-slot ticket">
                <OrderTicket />
              </div>
            </div>
          </div>
        </div>

        {/* ---------- status bar ---------- */}
        <footer style={{
          flexShrink: 0, height: 'var(--status-h)',
          display: 'flex', alignItems: 'center', gap: 'var(--sp-5)',
          padding: '0 var(--sp-4)',
          background: 'var(--bg-panel)', borderTop: '1px solid var(--border)',
          fontSize: 'var(--fs-xs)',
        }}>
          <Stat label="Day P&L" value={fmtSigned(totalPnl)}
                tone={totalPnl >= 0 ? 'profit' : 'loss'} />
          <Stat label="Exposure" value={fmtMoney(exposure, 'INR', true)} />
          <Stat label="Positions" value={String(positions.length)} />
          <Stat label="Working" value={String(working)} />

          <div style={{
            marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 'var(--sp-3)',
          }}>
            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-2xs)' }}>
              {mode !== 'LIVE'
                ? 'Simulated session — no real orders are sent'
                : 'Live session'}
            </span>
            <KillSwitch />
          </div>
        </footer>
      </main>

      <CommandPalette />
      <Licences open={licences} onClose={() => setLicences(false)} />
    </div>
  )
}

/** The reference's pill switch: sun on the knob in light, moon in dark. */
function ThemeSwitch({ theme, onToggle }: { theme: 'dark' | 'light'; onToggle: () => void }) {
  const dark = theme === 'dark'
  return (
    <button
      className={`qx-switch${dark ? '' : ' on'}`}
      role="switch"
      aria-checked={!dark}
      aria-label={dark ? 'Switch to light theme' : 'Switch to dark theme'}
      title={dark ? 'Switch to light theme' : 'Switch to dark theme'}
      onClick={onToggle}
    >
      <span className="knob"><Icon name={dark ? 'moon' : 'sun'} /></span>
    </button>
  )
}

function AccountMenu({ email, mode, onSignOut, onNavigate }: {
  email: string
  mode: string
  onSignOut?: () => void
  onNavigate: (p: Page) => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  const name = email.split('@')[0]
  const go = (p: Page) => { setOpen(false); onNavigate(p) }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button className="qx-account" onClick={() => setOpen((o) => !o)}
              aria-haspopup="menu" aria-expanded={open}>
        <span className="qx-avatar">{email.slice(0, 2).toUpperCase()}</span>
        <span style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
        <Icon name="chevron-down" size={14} />
      </button>
      {open && (
        <div className="qx-menu" role="menu">
          <div className="head">
            <b>{email}</b>
            <span>Trading mode: {mode} · IDA Tech-Solutions LLP</span>
          </div>
          <button className="qx-menu-item" role="menuitem" onClick={() => go('broking')}>
            <Icon name="link" size={15} /> Broking Details
          </button>
          <button className="qx-menu-item" role="menuitem" onClick={() => go('settings')}>
            <Icon name="settings" size={15} /> Settings
          </button>
          <button className="qx-menu-item" role="menuitem" onClick={() => go('help')}>
            <Icon name="help" size={15} /> Help
          </button>
          {onSignOut && (
            <button className="qx-menu-item danger" role="menuitem" onClick={onSignOut}>
              <Icon name="logout" size={15} /> Sign out
            </button>
          )}
        </div>
      )}
    </div>
  )
}

function Stat({ label, value, tone }: {
  label: string; value: string; tone?: 'profit' | 'loss'
}) {
  const color = tone === 'profit' ? 'var(--profit)'
              : tone === 'loss' ? 'var(--loss)' : 'var(--text-primary)'
  return (
    <span style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--sp-2)' }}>
      <span style={{
        fontSize: 'var(--fs-2xs)', textTransform: 'uppercase',
        letterSpacing: '0.06em', color: 'var(--text-muted)',
      }}>
        {label}
      </span>
      <span className="num" style={{ color, fontWeight: 700 }}>{value}</span>
    </span>
  )
}

export type { NavGroup, PageMeta }
